import { getSupabase, getSession, getUserId } from '../supabase-client.js';

const BUCKET = 'instance-files';
const MAX_BYTES = 15 * 1024 * 1024;
const SIGNED_URL_TTL_SEC = 3600;

const ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'application/pdf',
]);

function sanitizeFilename(name) {
  const base = String(name ?? 'file')
    .replace(/[/\\?%*:|"<>]/g, '_')
    .trim();
  return base.slice(0, 120) || 'file';
}

export function validateInstanceFile(file) {
  if (!file) throw new Error('Nessun file selezionato');
  const mime = file.type || 'application/octet-stream';
  if (!ALLOWED_TYPES.has(mime)) {
    throw new Error('Formato non supportato. Usa PDF, JPG o PNG.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('File troppo grande (max 15 MB).');
  }
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export { formatBytes as formatInstanceFileSize };

async function signedUrlForPath(storagePath) {
  const supabase = getSupabase();
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SEC);
  if (error) throw error;
  return data.signedUrl;
}

/**
 * @param {string} instanceId
 * @returns {Promise<Array<{ id: string, name: string, mime_type: string, size_bytes: number, created_at: string, url: string, catalog_index: number | null, ticket_item_id: string | null }>>}
 */
export async function listInstanceFiles(instanceId) {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('instance_files')
    .select('id, storage_path, name, mime_type, size_bytes, created_at, uploaded_by, catalog_index, ticket_item_id')
    .eq('instance_id', instanceId)
    .order('created_at', { ascending: false });
  if (error) throw error;

  const rows = data ?? [];
  return Promise.all(
    rows.map(async (row) => ({
      id: row.id,
      name: row.name,
      mime_type: row.mime_type,
      size_bytes: row.size_bytes,
      created_at: row.created_at,
      uploaded_by: row.uploaded_by,
      catalog_index: row.catalog_index,
      ticket_item_id: row.ticket_item_id,
      url: await signedUrlForPath(row.storage_path),
    })),
  );
}

/**
 * @param {string} instanceId
 * @param {File} file
 * @param {{ catalogIndex?: number | null, ticketItemId?: string | null }} [link]
 */
export async function uploadInstanceFile(instanceId, file, link = {}) {
  validateInstanceFile(file);

  const catalogIndex = link.catalogIndex ?? null;
  const ticketItemId = link.ticketItemId ?? null;
  if (catalogIndex != null && ticketItemId != null) {
    throw new Error('Il file può essere collegato a una sola voce.');
  }

  const supabase = getSupabase();
  const session = await getSession();
  const userId = getUserId(session);
  if (!userId) throw new Error('Sessione non valida');

  const fileId = crypto.randomUUID();
  const storagePath = `${instanceId}/${fileId}/${sanitizeFilename(file.name)}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, {
      contentType: file.type,
      upsert: false,
    });
  if (uploadError) throw uploadError;

  const insertRow = {
    instance_id: instanceId,
    storage_path: storagePath,
    name: file.name,
    mime_type: file.type,
    size_bytes: file.size,
    uploaded_by: userId,
  };
  if (catalogIndex != null) insertRow.catalog_index = catalogIndex;
  if (ticketItemId) insertRow.ticket_item_id = ticketItemId;

  const { data, error } = await supabase
    .from('instance_files')
    .insert(insertRow)
    .select('id')
    .single();

  if (error) {
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw error;
  }

  return data;
}

/**
 * @param {string} fileId
 * @param {string} instanceId
 * @param {{ catalogIndex?: number | null, ticketItemId?: string | null }} link
 */
export async function updateInstanceFileLink(fileId, instanceId, link) {
  const catalogIndex = link.catalogIndex ?? null;
  const ticketItemId = link.ticketItemId ?? null;
  if (catalogIndex != null && ticketItemId != null) {
    throw new Error('Il file può essere collegato a una sola voce.');
  }

  const supabase = getSupabase();
  const { error } = await supabase
    .from('instance_files')
    .update({
      catalog_index: catalogIndex,
      ticket_item_id: ticketItemId,
    })
    .eq('id', fileId)
    .eq('instance_id', instanceId);
  if (error) throw error;
}

/**
 * @param {string} fileId
 * @param {string} instanceId
 */
export async function deleteInstanceFile(fileId, instanceId) {
  const supabase = getSupabase();

  const { data: row, error: fetchError } = await supabase
    .from('instance_files')
    .select('storage_path')
    .eq('id', fileId)
    .eq('instance_id', instanceId)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!row) throw new Error('File non trovato');

  const { error: storageError } = await supabase.storage
    .from(BUCKET)
    .remove([row.storage_path]);
  if (storageError) throw storageError;

  const { error } = await supabase
    .from('instance_files')
    .delete()
    .eq('id', fileId)
    .eq('instance_id', instanceId);
  if (error) throw error;
}
